<template>
    <div
        class="linked-offer-row"
        id="row_{{props.linkedOffer.id}}"
        style="max-width: 100%; display: flex; justify-content: center"
    >
        <div class="row" style="margin: 1rem 0; max-width: 1400px">
            <div class="col mb-2" style="display: flex; align-items: center; column-gap: 1.5rem">
                <div style="min-width: 100px; text-align: end">{{ translations.linkOffer.childLocation }}</div>
                <Dropdown
                    :loading="loadingChildLocations"
                    style="width: 20rem"
                    v-model="selectedChildLocation"
                    :options="availableChildLocations"
                    :option-label="childLocationLabel"
                    @update:model-value="onUpdateSelectedChildLocation"
                />
            </div>
            <div class="col mb-2" style="display: flex; align-items: center; column-gap: 1.5rem">
                <div style="min-width: 100px; text-align: end">{{ translations.linkOffer.offers }}</div>
                <MultiSelect
                    class="linked-offers-multi-select"
                    :loading="loadingOffers"
                    style="width: 24rem"
                    display="chip"
                    v-model="selectedOffers"
                    :options="availableOffers"
                    :empty-message="translations.linkOffer.noAvailableOptions"
                    optionLabel="name"
                    filter
                    @update:model-value="onUpdateSelectedOffers"
                />

                <Button
                    @click="onClickRemoveRow"
                    style="background-color: #fd397a; border-color: #fd397a"
                    class="btn-danger"
                    icon="la la-trash"
                    aria-label="Cancel"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { Factory } from "@/data/dao/Factory";
    import { CatalogDao } from "@/data/dao/service/CatalogDao";
    import { LocationDao } from "@/data/dao/service/LocationDao";
    import type { ILinkedOffers, ILocationRelation } from "@/data/model/Location";
    import type { IOffer } from "@/data/model/Offer";
    import { CurrentLocationService } from "@/data/services/CurrentLocationService";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { onMounted, ref } from "vue";

    import Button from "primevue/button";
    import Dropdown from "primevue/dropdown";
    import MultiSelect from "primevue/multiselect";

    const locationDao = Factory.getInstance<LocationDao>(LocationDao);
    const catalogDao = Factory.getInstance<CatalogDao>(CatalogDao);

    const locationId = ref<string>(CurrentLocationService.getLocationId()!);
    const selectedChildLocation = ref<ILocationRelation | undefined>(undefined);
    const availableChildLocations = ref<Array<ILocationRelation>>([]);
    const availableOffers = ref<Array<IOffer>>([]);
    const selectedOffers = ref<Array<IOffer>>([]);

    const loadingChildLocations = ref<boolean>(false);
    const loadingOffers = ref<boolean>(false);

    // define props
    const props = defineProps<{
        linkedOffer: ILinkedOffers;
    }>();

    // define emits
    const emit = defineEmits(["onClickRemoveRow"]);

    async function onUpdateSelectedChildLocation(value: ILocationRelation) {
        // Reset values
        selectedOffers.value = [];
        props.linkedOffer.linkedOfferIds = [];
        props.linkedOffer.linkedLocationId = value.id;

        // load offers
        loadingOffers.value = true;

        try {
            const offers = await catalogDao.getOffers({ includeChildOffers: false, linkedLocationId: props.linkedOffer.linkedLocationId, orderBy: "CreatedDateTime" });
            availableOffers.value = offers.items.sort((a, b) => a.name > b.name ? 1 : -1);
        } catch (error) {
        } finally {
            loadingOffers.value = false;
        }
    }

    async function onUpdateSelectedOffers(value: IOffer[]) {
        props.linkedOffer.linkedOfferIds = value.map((o) => o.id);
    }

    function childLocationLabel(value: ILocationRelation): string {
        return value.childLocationInternalName ?? value.childLocationName ?? "";
    }

    function onClickRemoveRow() {
        emit("onClickRemoveRow", props.linkedOffer.id);
    }

    onMounted(async () => {
        // Load child locations
        try {
            loadingChildLocations.value = true;

            const locationRelations = await locationDao.getChildLocations(locationId.value);
            availableChildLocations.value = locationRelations;

            if (props.linkedOffer.linkedLocationId != null) {
                const childLocation = locationRelations.find(
                    (relation) => relation.id === props.linkedOffer.linkedLocationId
                );

                if (childLocation) {
                    selectedChildLocation.value = childLocation;
                }
            }
        } catch (error) {
        } finally {
            loadingChildLocations.value = false;
        }

        if ((props.linkedOffer?.linkedOfferIds ?? []).length > 0) {
            try {
                const offers = await catalogDao.getOffers({ includeChildOffers: false,  linkedLocationId: props.linkedOffer.linkedLocationId, orderBy: "CreatedDateTime" });
                availableOffers.value = offers.items;

                let newSelectedOffers = new Array<IOffer>();

                for (let linkedOfferId of props.linkedOffer.linkedOfferIds) {
                    const linkedOffer = offers.items.find((offer) => offer.id === linkedOfferId);
                    if (linkedOffer) {
                        newSelectedOffers.push(linkedOffer);
                    }
                }

                selectedOffers.value = newSelectedOffers;
            } catch (error) {
                logError(error);
            } finally {
                loadingOffers.value = false;
            }
        }
    });
</script>

<style scoped lang="scss">
    @use "./../../../../../assets/styles/scss/shared/colors.scss";

    .linked-offer-row {
        ::v-deep(.p-multiselect-label) {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;

            .p-multiselect-token {
                vertical-align: top;
                border-radius: 4px;
                padding: 0 28px 0 10px;
                line-height: 30px;
                font-size: 13px;
                background-color: white;
                box-sizing: border-box;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                color: colors.$fontColor;
                border: 1px solid colors.$green;
                margin: 3px;
            }
        }
    }
</style>